<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { toTypedSchema } from '@vee-validate/zod'
import { Download, Loader2 } from 'lucide-vue-next'
import HorizontalLine from 'pejer/src/components/HorizontalLine.vue'
import { AutoForm } from 'pejer/src/components/ui/auto-form'
import { Button } from 'pejer/src/components/ui/button'
import { toast } from 'pejer/src/components/ui/toast'
import usePejerMutation from 'pejer/src/usePejerMutation'
import { useForm } from 'vee-validate'
import { computed, watch } from 'vue'
import { z } from 'zod'

const props = defineProps<{
  businessName: string
  organizationId: string
  countryCode: string
  bban: string
  contactPostalCode: string
  contactPersonName: string
  contactPersonEmail: string
}>()

const emit = defineEmits<{
  (e: 'update:finished', value: boolean): void
}>()

const pejer = usePejer()
const formSchema = z.object({
  textOnAccountStatement: z.string().min(1).max(30),
})

const suggestedStatementText = computed(() => (props.businessName ?? '').slice(0, 30))

const form = useForm({
  validationSchema: toTypedSchema(formSchema),
})

watch(
  suggestedStatementText,
  (suggestion) => {
    if (suggestion && !form.values.textOnAccountStatement)
      form.setFieldValue('textOnAccountStatement', suggestion)
  },
  { immediate: true }
)

const hasFormErrors = computed(() => !formSchema.safeParse(form.values).success)

watch(form.values, () => {
  agreementDownloadMutation.reset()
})

const agreementDownloadMutation = usePejerMutation(
  pejer.urls.businessExternalAccounts.createAgreementPDF,
  {
    queryKeys: [['businessExternalAccounts', 'agreement', 'PDF']],
    onSuccess: (data) => {
      toast({
        title: 'Avtalen er lastet ned',
      })

      const a = document.createElement('a')
      a.href = data
      a.download = 'Registreringsskjema-Nets.pdf'
      a.click()
    },
    onError: (error) => {
      toast({
        title: 'Kunne ikke hente avtalen',
        description:
          'Vi klarte ikke å generere det ferdig utfylte skjemaet. Prøv igjen senere, eller ta kontakt med oss.',
        variant: 'destructive',
      })
    },
  }
)
watch(
  () => [hasFormErrors.value, agreementDownloadMutation.data.value],
  () => {
    emit(
      'update:finished',
      formSchema.safeParse(form.values).success && !!agreementDownloadMutation.data.value
    )
  }
)

const downloadAgreement = async () => {
  await agreementDownloadMutation.mutateAsync({
    body: {
      bban: props.bban,
      organizationID: props.organizationId,
      countryCode: props.countryCode,
      contactPostalCode: props.contactPostalCode,
      contactPersonName: props.contactPersonName,
      contactPersonEmail: props.contactPersonEmail,
      ...form.values,
    },
  })
}

// const sendToBank = () => {
//   const greeting = form.values.bankContactPerson ? `Hei ${form.values.bankContactPerson},` : 'Hei,'

//   const emailBody = `${greeting}

// Vi ønsker å opprette en OCR/KID-avtale på følgende konto: ${props.bban}
// Bedrift: ${props.businessName} (org.nr. ${props.organizationId})

// Se vedlagt "Registreringsskjema for Nets-tjenester". Skjemaet er ferdig utfylt.
// ${agreementDownloadMutation.data.value ? `Det kan også lastes ned herfra: ${agreementDownloadMutation.data.value}` : ''}
// `

//   const subject = `${props.businessName}: opprette OCR/KID-avtale på konto ${props.bban}`
//   window.open(
//     `mailto:${form.values.bankEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`,
//     '_blank'
//   )
// }
</script>
<template>
  <div
    class="flex flex-col gap-1 p-3 bg-slate-50 rounded-md border border-slate-200 [&_input]:bg-white"
  >
    <span class="text-slate-600 text-sm">
      Banken din må registrere en OCR/KID-avtale hos Nets slik at innbetalinger til kontoen
      rapporteres til oss. Vi fyller ut skjemaet for deg — du sender det videre til banken.
    </span>

    <!-- <h3 class="font-semibold mt-4">1. Opprett en ny bankkonto i nettbanken</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <span class="text-slate-600 text-sm">
      Opprett en helt ny konto som kun skal brukes til dette formålet. Det gjør du som regel selv i
      nettbanken. Ikke bruk en konto som allerede er i bruk til noe annet.
    </span> -->

    <h3 class="font-semibold mt-4">1. Fyll ut opplysningene vi mangler</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <AutoForm
      :schema="formSchema"
      :form="form"
      class="flex flex-col gap-4 mb-2"
      :field-config="{
        textOnAccountStatement: {
          label: 'Tekst på kontoutskrift',
          description:
            'Dette ser medlemmet på kontoutskriften sin når pengene trekkes. Maks 30 tegn.',
          inputProps: { placeholder: suggestedStatementText || 'Navnet på klubben' },
        },
      }"
    />

    <h3 class="font-semibold mt-4">2. Last ned ferdig utfylt avtale</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <Button
      @click.prevent.stop="downloadAgreement"
      variant="default"
      class="group w-fit"
      :disabled="hasFormErrors || agreementDownloadMutation.isPending.value"
    >
      <Loader2
        v-if="agreementDownloadMutation.isPending.value"
        class="mr-2 animate-spin"
        :size="16"
      />
      <Download v-else class="group-hover:scale-110 mr-2" :size="16" />
      Last ned avtale
    </Button>
    <span v-if="agreementDownloadMutation.isError.value" class="text-slate-500 text-sm mt-2">
      Får du ikke lastet ned skjemaet? Ta kontakt med oss, så sender vi det til deg.
    </span>

    <h3 class="font-semibold mt-4">3. Send avtalen til banken</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <p class="text-sm text-muted-foreground">
      Avtalen er klar og kan nå sendes til banken.
      <template v-if="agreementDownloadMutation.data.value">
        Her er et forslag til epost som kan sendes:
      </template>
    </p>
    <p
      class="bg-white rounded-lg border-slate-200 border p-2 font-mono text-sm relative overflow-hidden"
      :style="{
        'max-height': agreementDownloadMutation.data.value ? '100%' : '0',
        opacity: agreementDownloadMutation.data.value ? '1' : '0',
      }"
    >
      Emne: {{ props.businessName }}: opprette OCR/KID-avtale på konto {{ props.bban }}<br /><br />

      Hei,<br /><br />
      Vi ønsker å opprette en OCR/KID-avtale på følgende konto: {{ props.bban }}<br />
      Bedrift: {{ props.businessName }} (org.nr. {{ props.organizationId }})<br /><br />
      <!-- Se vedlagt "Registreringsskjema for Nets-tjenester". Skjemaet er ferdig utfylt.<br /> -->
      {{
        agreementDownloadMutation.data.value
          ? `Skjemaet kan lastes ned herfra: ${agreementDownloadMutation.data.value}`
          : ''
      }}<br /><br />
      Vennlig hilsen,<br />
      {{ props.businessName }}
      <!-- <Button variant="outline" class="group w-fit mt-2 absolute bottom-2 right-2 font-sans">
        <Mail class="group-hover:scale-110 mr-2" :size="16" />
        Åpne epost
      </Button> -->
    </p>
  </div>
</template>
